// Card da lista de White Labels (§6.1): logo com fallback de
// iniciais, nome, slug mono, loja, badge Ativo/Inativo, 5 swatches
// das cores do tema e CTA "Configurar" → detalhe.
import { ChevronRightIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { AdminWhiteLabelListItem } from "@/types/admin-white-labels"

import { colorSwatches, getInitials } from "../utils"

const panelPrefix = "pages.adminWhiteLabels"

// Badge Ativo/Inativo compartilhado da feature.
export const ActiveBadge = ({
  active,
}: {
  active: boolean
}) => {
  const { t } = useTranslation()
  return (
    <Badge
      variant="outline"
      className={cn(
        "rounded-full border font-medium",
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-zinc-200 bg-zinc-50 text-zinc-500"
      )}
    >
      {t(
        `${panelPrefix}.${active ? "badgeActive" : "badgeInactive"}`
      )}
    </Badge>
  )
}

// Fileira dos 5 swatches do tema (slot vazio p/ cor ausente).
export const ColorSwatches = ({
  colors,
}: {
  colors: AdminWhiteLabelListItem["colors"]
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-1.5">
      {colorSwatches(colors).map(({ key, value }) => (
        <span
          key={key}
          data-testid={`color-swatch-${key}`}
          title={`${t(`${panelPrefix}.theme.colors.${key}`)}: ${value ?? t(`${panelPrefix}.theme.colorUndefined`)}`}
          className={cn(
            "h-4 w-4 rounded border",
            !value && "border-dashed bg-muted"
          )}
          style={
            value ? { backgroundColor: value } : undefined
          }
        />
      ))}
    </div>
  )
}

type WhiteLabelCardProps = {
  whiteLabel: AdminWhiteLabelListItem
}

export const WhiteLabelCard = ({
  whiteLabel,
}: WhiteLabelCardProps) => {
  const { t } = useTranslation()

  return (
    <div
      data-testid={`white-label-card-${whiteLabel.uuid}`}
      className={cn(
        // Entity card (rule 03): acento a esquerda pelo estado — o operador
        // separa ativo de inativo varrendo a grade, sem ler o badge de cada um.
        // hover:shadow-md da o retorno de que o card e uma superficie acionavel.
        "flex flex-col gap-3 rounded-xl border border-l-4 bg-card p-4 shadow-sm transition-shadow hover:shadow-md",
        whiteLabel.active
          ? "border-l-emerald-500"
          : "border-l-zinc-300"
      )}
    >
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 rounded-lg">
          {whiteLabel.logoUrl && (
            <AvatarImage
              src={whiteLabel.logoUrl}
              alt={whiteLabel.name}
            />
          )}
          <AvatarFallback className="rounded-lg text-xs font-semibold">
            {getInitials(whiteLabel.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold">
            {whiteLabel.name}
          </span>
          <span className="truncate font-mono text-xs text-muted-foreground">
            {whiteLabel.slug}
          </span>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="truncate text-xs text-muted-foreground">
          {t(`${panelPrefix}.storeLabel`)}:{" "}
          {whiteLabel.storeName ?? "—"}
        </span>
        <ActiveBadge active={whiteLabel.active} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <ColorSwatches colors={whiteLabel.colors} />
        <Button asChild variant="outline" size="sm">
          <Link
            to={`/admin/white-labels/${whiteLabel.uuid}`}
          >
            {t(`${panelPrefix}.configure`)}
            <ChevronRightIcon
              className="ml-1 size-4"
              aria-hidden="true"
            />
          </Link>
        </Button>
      </div>
    </div>
  )
}
